<template>
    <Modal size="md" @close="$emit('close')">
        <template #header>
            <div class="text-gray-600">Export to Zoho Campaigns</div>
        </template>

        <template #body>
            <div class="p-6">
                <form>
                    <MultipleSelect
                        v-model:value="state.lists"
                        placeholder="Choose lists"
                        :options="lists"
                        label="Contact lists"
                        required
                        :error="getError('lists')"
                        :disabled="loading"
                    />

                    <SelectInput
                        v-model="state.topic_id"
                        class="mt-6"
                        placeholder="Choose topic"
                        :data="topics"
                        label="Topic"
                        required
                        :error="getError('topic_id')"
                        :disabled="loading"
                        search
                        @blurred="v$.topic_id.$touch()"
                    />
                </form>

                <div class="mt-6">
                    <div class="space-y-3 mt-1 text-xs text-gray-600" >
                    <p class="">
                        To export your email template to Zoho, you need to have at least one contact list and one topic created in your Zoho account.
                    </p>
                    <div>
                        <p class="font-semibold">To add it in Zoho Campaigns:</p>
                        <ul class="list-disc list-inside space-y-1">
                        <li>Go to <span class="font-medium">Contacts → Manage Lists</span> → click <span class="font-medium">Create List</span> → name your list and save.</li>
                        <li>Go to <span class="font-medium">Contacts → Manage Topics</span> → click <span class="font-medium">Add Topic</span> → enter the topic name/description and save.</li>
                        </ul>
                    </div>
                    </div>
                </div>

            </div>
        </template>

        <template #footer>
            <div class="flex justify-between border-t border-gray-200 p-6">
                <AtomicButton color="white" size="md" @click="$emit('close')">
                    Cancel
                </AtomicButton>

                <AtomicButton
                    size="md"
                    color="yellow"
                    :disabled="loading || v$.$invalid"
                    @click="exportToZohoCampaigns"
                >
                    Export
                </AtomicButton>
            </div>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { PropType, reactive, ref } from 'vue';
import {
    IIntegration,
    IZohoCampaigns,
} from '@/types/Integrations/IIntegration';
import axios from 'axios';
import { route } from 'ziggy-js';
import { create } from '@composables/useNotifications';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import ISelectable from '@/types/Atomic/ISelectable';

const emit = defineEmits(['close']);

const props = defineProps({
    integration: {
        required: true,
        type: Object as PropType<IIntegration<IZohoCampaigns>>,
    },
    templateId: {
        required: true,
        type: Number,
    },
    templateName: {
        required: true,
        type: String,
    },
});

const state = reactive({
    topic_id: '',
    lists: [],
});

const rules = {
    topic_id: {
        required,
    },
    lists: {
        required,
    },
};
const v$ = useVuelidate(rules, state);

const loading = ref(false);
const topics = ref<ISelectable[]>([]);
const lists = ref<ISelectable[]>([]);
const openZohoListsAndTopicsInfo = ref(false);

onMounted(() => {
    getLists();
    getTopics();
});

const getLists = async () => {
    try {
        loading.value = true;
        const res = await axios.get(
            route('integrations.zoho-campaigns.lists.show', {
                integration: props.integration.id,
            })
        );

        if (res.data.success === true) {
            lists.value = res.data.data;
        }
    } catch (e) {
        return;
    } finally {
        loading.value = false;
    }
};

const getTopics = async () => {
    try {
        loading.value = true;
        const res = await axios.get(
            route('integrations.zoho-campaigns.topics.show', {
                integration: props.integration.id,
            })
        );

        if (res.data.success === true) {
            topics.value = res.data.data;
        }
    } catch (e) {
        return;
    } finally {
        loading.value = false;
    }
};

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || '';
};

const exportToZohoCampaigns = async () => {
    loading.value = true;
    const lists = state.lists.map((list: ISelectable) => list.value);

    try {
        const response = await axios.post(
            route('templates.exports.zoho-campaigns', {
                template: props.templateId,
            }),
            {
                topic_id: state.topic_id,
                lists: lists,
                integration_id: props.integration.id,
            }
        );

        if (response.data.success) {
            create({
                title: 'Successful export',
                text: 'Template has been successfully exported to Zoho Campaigns.',
                type: 'success',
            });
            emit('close');

            return;
        }
        create({
            title: 'Unsuccessful export',
            text: 'Template has not been exported to Zoho Campaigns.',
            type: 'error',
        });
    } catch (e) {
        return;
    } finally {
        loading.value = false;
    }
};
</script>
